// =============================================================================
// components/KpiIncidents/IncidentMap.jsx
//
// Leaflet map with site markers. Performance strategy:
//   - Canvas renderer (preferCanvas) — much faster than SVG for thousands
//     of shapes.
//   - Imperative layer in SitesLayer — zero React overhead per marker.
//   - setStyle() to highlight/restore markers without a re-render.
//
// Defensiveness against React 18 Strict Mode + the Leaflet lifecycle:
//   - mountedRef prevents operations after unmount.
//   - guards on every useEffect that touches Leaflet refs.
//   - cleanup ignores errors if the layer has already been removed.
// =============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box, Paper, Typography, IconButton, Tooltip as MuiTooltip, Chip, Stack, Button,
} from '@mui/material';
import LayersIcon          from '@mui/icons-material/Layers';
import LayersClearIcon     from '@mui/icons-material/LayersClear';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import LocationOffIcon     from '@mui/icons-material/LocationOff';
import CloseIcon           from '@mui/icons-material/Close';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import { MapContainer, TileLayer, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

// -----------------------------------------------------------------------------
// Configuration
// -----------------------------------------------------------------------------
const DEFAULT_CENTER = [54.5, -3.5];
const DEFAULT_ZOOM   = 5;
const SELECTED_ZOOM  = 17;
const MAP_MAX_ZOOM   = 21;

const TILE_LAYERS = {
	street: {
		url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
		attribution: '&copy; OpenStreetMap',
		maxNativeZoom: 19,
		maxZoom: MAP_MAX_ZOOM,
	},
	satellite: {
		url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
		attribution: '&copy; Esri',
		maxNativeZoom: 19,
		maxZoom: MAP_MAX_ZOOM,
	},
};

// -----------------------------------------------------------------------------
// Pure helpers
// -----------------------------------------------------------------------------
function colorForSeverity(severityMap, sev) {
	if (severityMap && severityMap[sev] && severityMap[sev].colorHex) {
		return severityMap[sev].colorHex;
	}
	return '#888888';
}

function radiusForCount(count, isSelected) {
	if (isSelected) return 8;
	if (count >= 5) return 6;
	if (count >= 3) return 5;
	if (count >= 2) return 4;
	return 3;
}

function styleForSite(site, isSelected, severityMap) {
	const color = colorForSeverity(severityMap, site.worstSeverity);
	return {
		radius:      radiusForCount(site.count, isSelected),
		color:       color,
		fillColor:   color,
		fillOpacity: isSelected ? 0.9 : 0.7,
		weight:      isSelected ? 2 : 1,
	};
}

function tooltipHtmlForSite(site, severityMap) {
	const color    = colorForSeverity(severityMap, site.worstSeverity);
	const sevLabel = (severityMap && severityMap[site.worstSeverity])
		? severityMap[site.worstSeverity].label : '—';

	const name   = site.cellSiteName
		? '<div style="color:#475569">' + site.cellSiteName + '</div>' : '';
	const cities = site.cities
		? '<div style="color:#64748b;font-size:0.7rem">' + site.cities + '</div>' : '';

	return (
		'<div style="font-family:Arial;font-size:0.75rem">' +
		'<div style="font-weight:700">' + site.siteId + '</div>' +
		name + cities +
		'<div style="margin-top:4px">' +
		'<span style="color:' + color + ';font-weight:600">' + sevLabel + '</span>' +
		' · ' + site.count + ' incident' + (site.count > 1 ? 's' : '') +
		'</div></div>'
	);
}

function clusterIconCreate(cluster) {
	const count = cluster.getChildCount();
	let color;
	if (count >= 100)      color = '#7f1d1d';
	else if (count >= 25)  color = '#b91c1c';
	else if (count >= 10)  color = '#dc2626';
	else if (count >= 5)   color = '#ea580c';
	else                   color = '#0891b2';

	const size = count < 10 ? 28 : count < 100 ? 34 : 42;
	const html =
		'<div style="' +
		'width:' + size + 'px;height:' + size + 'px;' +
		'border-radius:50%;background:' + color + ';' +
		'border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);' +
		'display:flex;align-items:center;justify-content:center;' +
		'color:#fff;font-family:Arial;font-weight:700;' +
		'font-size:' + (count < 100 ? 12 : 11) + 'px;opacity:0.92;' +
		'">' + count + '</div>';

	return L.divIcon({
		html: html,
		className: 'tkad-cluster-icon',
		iconSize: L.point(size, size),
	});
}

// -----------------------------------------------------------------------------
// MapController — programmatic setView (triggered by a click in the table)
// -----------------------------------------------------------------------------
function MapController({ flyTo }) {
	const map = useMap();
	useEffect(function () {
		if (!map || !flyTo || flyTo.lat == null || flyTo.lng == null) return;
		try {
			map.setView(
				[flyTo.lat, flyTo.lng],
				flyTo.zoom || SELECTED_ZOOM,
				{ animate: false }
			);
		} catch (e) {}
	}, [flyTo, map]);
	return null;
}

// -----------------------------------------------------------------------------
// MapResizeWatcher
// -----------------------------------------------------------------------------
function MapResizeWatcher() {
	const map = useMap();
	useEffect(function () {
		if (!map) return;
		const container = map.getContainer();
		if (!container || !container.parentElement) return;
		if (typeof ResizeObserver === 'undefined') return;

		const parent = container.parentElement;
		let lastW = parent.clientWidth;
		let lastH = parent.clientHeight;
		let timer = null;
		let cancelled = false;

		const observer = new ResizeObserver(function () {
			if (cancelled) return;
			const w = parent.clientWidth;
			const h = parent.clientHeight;
			if (Math.abs(w - lastW) < 2 && Math.abs(h - lastH) < 2) return;
			lastW = w; lastH = h;
			if (timer != null) clearTimeout(timer);
			timer = setTimeout(function () {
				if (cancelled) return;
				try { map.invalidateSize(false); } catch (e) {}
				timer = null;
			}, 100);
		});
		observer.observe(parent);

		const initialTimer = setTimeout(function () {
			if (cancelled) return;
			try { map.invalidateSize(false); } catch (e) {}
		}, 200);

		return function () {
			cancelled = true;
			observer.disconnect();
			if (timer != null) clearTimeout(timer);
			clearTimeout(initialTimer);
		};
	}, [map]);
	return null;
}

// -----------------------------------------------------------------------------
// SitesLayer — imperatively manages the cluster and markers
// -----------------------------------------------------------------------------
function SitesLayer({ sites, severityMap, selectedSiteId, onSiteClick }) {
	const map = useMap();

	const clusterRef     = useRef(null);
	const rendererRef    = useRef(null);
	const markersRef     = useRef(new Map());
	const mountedRef     = useRef(false);
	const onClickRef     = useRef(onSiteClick);
	const severityMapRef = useRef(severityMap);

	// Live refs for props that change frequently without rebuilding the layer
	useEffect(function () { onClickRef.current = onSiteClick; });
	useEffect(function () { severityMapRef.current = severityMap; });

	// ---- mount/unmount: creates the cluster and renderer once ---------------
	useEffect(function () {
		if (!map) return;

		const renderer = L.canvas({ padding: 0.5 });
		const cluster  = L.markerClusterGroup({
			chunkedLoading:             true,
			chunkInterval:              100,
			chunkDelay:                 30,
			maxClusterRadius:           50,
			spiderfyOnMaxZoom:          false,
			showCoverageOnHover:        false,
			animate:                    false,
			animateAddingMarkers:       false,
			removeOutsideVisibleBounds: true,
			disableClusteringAtZoom:    15,
			iconCreateFunction:         clusterIconCreate,
		});

		rendererRef.current = renderer;
		clusterRef.current  = cluster;
		mountedRef.current  = true;

		// Defer addLayer by one tick so React 18 Strict Mode's second mount
		// doesn't try to add a layer to a map that was just torn down.
		const t = setTimeout(function () {
			if (!mountedRef.current) return;
			try { map.addLayer(cluster); } catch (e) {}
		}, 0);

		return function () {
			clearTimeout(t);
			mountedRef.current = false;
			try {
				if (cluster._map) map.removeLayer(cluster);
			} catch (e) {}
			markersRef.current.clear();
			if (clusterRef.current === cluster)  clusterRef.current = null;
			if (rendererRef.current === renderer) rendererRef.current = null;
		};
	}, [map]);

	// ---- populate markers when sites changes ---------------------------------
	useEffect(function () {
		const cluster  = clusterRef.current;
		const renderer = rendererRef.current;
		if (!mountedRef.current || !cluster || !renderer) return;

		function populate() {
			if (!mountedRef.current) return;
			const c = clusterRef.current;
			const r = rendererRef.current;
			if (!c || !r || !c._map) return;

			try { c.clearLayers(); } catch (e) {}
			markersRef.current.clear();

			if (!sites || sites.length === 0) return;

			const layers = [];
			for (let i = 0; i < sites.length; i++) {
				const site = sites[i];
				const isSelected = site.siteId === selectedSiteId;
				const marker = L.circleMarker(
					[site.latitude, site.longitude],
					Object.assign(
						{ renderer: r },
						styleForSite(site, isSelected, severityMapRef.current)
					)
				);
				marker.bindTooltip(
					tooltipHtmlForSite(site, severityMapRef.current),
					{ direction: 'top', offset: [0, -6] }
				);
				marker.on('click', function () {
					if (!mountedRef.current) return;
					const cb = onClickRef.current;
					if (cb) cb(site);
				});
				markersRef.current.set(site.key, marker);
				layers.push(marker);
			}
			try { c.addLayers(layers); } catch (e) {}
		}

		// If the cluster isn't attached yet (deferred addLayer), retry after
		// the setTimeout(0) in the mount effect has fired.
		if (!cluster._map) {
			const t = setTimeout(populate, 20);
			return function () { clearTimeout(t); };
		}

		populate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sites]);

	// ---- highlight the selected marker (without a React re-render) ----------
	const prevSelectedRef = useRef(null);
	useEffect(function () {
		const cluster = clusterRef.current;
		if (!mountedRef.current || !cluster || !cluster._map) return;

		const markers = markersRef.current;
		const prev = prevSelectedRef.current;

		if (prev) {
			const prevSite = sites.find(function (s) { return s.siteId === prev; });
			if (prevSite) {
				const prevMarker = markers.get(prevSite.key);
				if (prevMarker) {
					try {
						prevMarker.setStyle(styleForSite(prevSite, false, severityMapRef.current));
					} catch (e) {}
				}
			}
		}

		if (selectedSiteId) {
			const newSite = sites.find(function (s) { return s.siteId === selectedSiteId; });
			if (newSite) {
				const newMarker = markers.get(newSite.key);
				if (newMarker) {
					try {
						newMarker.setStyle(styleForSite(newSite, true, severityMapRef.current));
						newMarker.bringToFront();
					} catch (e) {}
				}
			}
		}

		prevSelectedRef.current = selectedSiteId;
	}, [selectedSiteId, sites]);

	return null;
}

// =============================================================================
// IncidentMap — public component
// =============================================================================
export default function IncidentMap({
	items,
	siteMap,
	severityMap,
	selectedSiteId,
	selectedIncident,      // incident clicked in the table (for the header)
	flyToCoords,
	onSiteClick,
	onClearSelection,
	onIncidentSelect,      // called when a marker with exactly 1 incident is clicked
}) {
	const [tileMode, setTileMode] = useState('street');
	const mapRef = useRef(null);

	// -------------------------------------------------------------------------
	// Aggregate incidents by site
	// -------------------------------------------------------------------------
	const sitesWithIncidents = useMemo(function () {
		const result = new Map();
		(items || []).forEach(function (inc) {
			const key = inc.vendor + '|' + inc.siteId;
			const site = siteMap.get(key);
			if (!site) return;

			let entry = result.get(key);
			if (!entry) {
				entry = {
					key:           key,
					vendor:        site.vendor,
					siteId:        site.siteId,
					cellSiteName:  site.cellSiteName,
					cities:        site.cities,
					latitude:      site.latitude,
					longitude:     site.longitude,
					count:         0,
					worstSeverity: 99,
				};
				result.set(key, entry);
			}
			entry.count += 1;
			if (inc.severity != null && inc.severity < entry.worstSeverity) {
				entry.worstSeverity = inc.severity;
			}
		});
		return Array.from(result.values());
	}, [items, siteMap]);

	const incidentsWithoutCoords = useMemo(function () {
		if (!items || !siteMap) return 0;
		let n = 0;
		for (let i = 0; i < items.length; i++) {
			const inc = items[i];
			if (!siteMap.has(inc.vendor + '|' + inc.siteId)) n += 1;
		}
		return n;
	}, [items, siteMap]);

	// -------------------------------------------------------------------------
	// Click on a marker
	// -------------------------------------------------------------------------
	const handleMarkerClick = function (site) {
		const isCurrentlySelected = site.siteId === selectedSiteId;
		if (mapRef.current && !isCurrentlySelected) {
			try {
				mapRef.current.setView(
					[site.latitude, site.longitude],
					SELECTED_ZOOM,
					{ animate: false }
				);
			} catch (e) {}
		}
		onSiteClick(site);

		// Auto-select the incident when the site has exactly one — opens the
		// detail panel immediately, same as clicking the row in the table.
		// When the site has multiple incidents the user picks from the table.
		if (site.count === 1 && onIncidentSelect) {
			const match = (items || []).find(function (inc) {
				return inc.siteId === site.siteId && inc.vendor === site.vendor;
			});
			if (match) onIncidentSelect(match);
		}
	};

	// -------------------------------------------------------------------------
	// Focused state: marker OR incident from the table
	// -------------------------------------------------------------------------
	const isFocused = !!(selectedSiteId || flyToCoords);

	useEffect(function () {
		setTileMode(isFocused ? 'satellite' : 'street');
	}, [isFocused]);

	const prevFocusRef = useRef(false);
	useEffect(function () {
		const wasFocused = prevFocusRef.current;
		if (wasFocused && !isFocused && mapRef.current) {
			try {
				mapRef.current.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
			} catch (e) {}
		}
		prevFocusRef.current = isFocused;
	}, [isFocused]);

	// -------------------------------------------------------------------------
	// Toolbar handlers
	// -------------------------------------------------------------------------
	const handleResetView = function () {
		if (onClearSelection) onClearSelection(); // clears state, useEffect handles the view
	};

	const tile = TILE_LAYERS[tileMode];
	const selectedSiteFromMap = sitesWithIncidents.find(function (s) {
		return s.siteId === selectedSiteId;
	});

	// -------------------------------------------------------------------------
	// Header content — contextual depending on what is focused
	// -------------------------------------------------------------------------
	const headerContent = (function () {
		if (selectedIncident) {
			return (
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
						<Typography variant="caption" fontWeight={700} sx={{
							textTransform: 'uppercase', letterSpacing: 0.4,
							color: '#1e40af', whiteSpace: 'nowrap',
						}}>
							{selectedIncident.siteId}
						</Typography>
						<Typography variant="caption" sx={{
							color: '#64748b', fontFamily: 'monospace',
							fontSize: '0.72rem', whiteSpace: 'nowrap',
							overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							· {selectedIncident.cellId}
						</Typography>
					</Box>
					{selectedIncident.kpiName && (
						<Typography variant="caption" sx={{
							color: '#475569', fontWeight: 600,
							fontSize: '0.7rem', display: 'block',
							overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
						}}>
							{selectedIncident.kpiName}
						</Typography>
					)}
				</Box>
			);
		}
		if (selectedSiteFromMap) {
			return (
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
						<Typography variant="caption" fontWeight={700} sx={{
							textTransform: 'uppercase', letterSpacing: 0.4,
							color: '#1e40af', whiteSpace: 'nowrap',
						}}>
							{selectedSiteFromMap.siteId}
						</Typography>
						<Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>
							· {selectedSiteFromMap.count} incident{selectedSiteFromMap.count > 1 ? 's' : ''}
						</Typography>
					</Box>
					{selectedSiteFromMap.cellSiteName && (
						<Typography variant="caption" sx={{
							color: '#64748b', fontSize: '0.7rem', display: 'block',
							overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
						}}>
							{selectedSiteFromMap.cellSiteName}
						</Typography>
					)}
				</Box>
			);
		}
		return (
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Typography variant="caption" fontWeight={700} sx={{
					textTransform: 'uppercase', letterSpacing: 0.4, color: '#475569',
				}}>
					Map · {sitesWithIncidents.length} sites
				</Typography>
			</Box>
		);
	})();

	// -------------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------------
	return (
		<Paper
			variant="outlined"
			sx={{
				height: '100%',
				display: 'flex', flexDirection: 'column',
				overflow: 'hidden', position: 'relative',
			}}
		>
			{/* Header — contextual */}
			<Box sx={{
				px: 1.5, py: 0.75,
				bgcolor: isFocused ? '#eff6ff' : '#fafbfc',
				borderBottom: '1px solid #e5e7eb',
				display: 'flex', alignItems: 'center', gap: 1,
				transition: 'background-color 0.15s',
				minWidth: 0,
			}}>
				{headerContent}
				{isFocused && (
					<Chip
						size="small"
						label="Clear"
						onDelete={onClearSelection}
						deleteIcon={<CloseIcon />}
						sx={{
							flexShrink: 0,
							height: 22, fontSize: '0.7rem', fontWeight: 600,
							bgcolor: '#1e40af', color: 'white',
							'& .MuiChip-deleteIcon': {
								color: 'rgba(255,255,255,0.85)',
								'&:hover': { color: 'white' },
							},
						}}
					/>
				)}
			</Box>

			{/* Map */}
			<Box sx={{ flex: 1, position: 'relative', minHeight: 400 }}>
				<MapContainer
					center={DEFAULT_CENTER}
					zoom={DEFAULT_ZOOM}
					maxZoom={MAP_MAX_ZOOM}
					preferCanvas={true}
					style={{ height: '100%', width: '100%' }}
					zoomControl={false}
					ref={mapRef}
				>
					<TileLayer
						url={tile.url}
						attribution={tile.attribution}
						maxNativeZoom={tile.maxNativeZoom}
						maxZoom={tile.maxZoom}
					/>
					<ZoomControl position="bottomright" />

					<MapController flyTo={flyToCoords} />
					<MapResizeWatcher />

					<SitesLayer
						sites={sitesWithIncidents}
						severityMap={severityMap}
						selectedSiteId={selectedSiteId}
						onSiteClick={handleMarkerClick}
					/>
				</MapContainer>

				{/* "Back to overview" — visible for ANY focused state */}
				{isFocused && (
					<Box sx={{
						position: 'absolute', top: 12, left: '50%',
						transform: 'translateX(-50%)', zIndex: 1000,
					}}>
						<Button
							variant="contained"
							size="small"
							startIcon={<ArrowBackIcon />}
							onClick={onClearSelection}
							sx={{
								bgcolor: 'white', color: '#1e293b',
								boxShadow: 2, textTransform: 'none',
								fontWeight: 600, fontSize: '0.78rem',
								px: 1.5, py: 0.5,
								'&:hover': { bgcolor: '#f1f5f9', boxShadow: 3 },
							}}
						>
							Back to overview
						</Button>
					</Box>
				)}

				{/* Toolbar */}
				<Stack
					direction="column"
					spacing={0.5}
					sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}
				>
					<MuiTooltip
						title={tileMode === 'street' ? 'Switch to satellite' : 'Switch to street'}
						placement="left"
					>
						<IconButton
							size="small"
							onClick={function () {
								setTileMode(function (m) { return m === 'street' ? 'satellite' : 'street'; });
							}}
							sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#f8fafc' } }}
						>
							{tileMode === 'street'
								? <LayersIcon fontSize="small" />
								: <LayersClearIcon fontSize="small" />}
						</IconButton>
					</MuiTooltip>

					<MuiTooltip title="Reset view" placement="left">
						<IconButton
							size="small"
							onClick={handleResetView}
							sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#f8fafc' } }}
						>
							<CenterFocusStrongIcon fontSize="small" />
						</IconButton>
					</MuiTooltip>
				</Stack>

				{incidentsWithoutCoords > 0 && (
					<Box sx={{
						position: 'absolute', bottom: 8, left: 8, zIndex: 1000,
						bgcolor: 'rgba(255,255,255,0.95)',
						border: '1px solid #e5e7eb', borderRadius: 1,
						px: 1, py: 0.5,
						display: 'flex', alignItems: 'center', gap: 0.5,
						boxShadow: 1,
					}}>
						<LocationOffIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
						<Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#475569' }}>
							{incidentsWithoutCoords} incident{incidentsWithoutCoords > 1 ? 's' : ''} without a location
						</Typography>
					</Box>
				)}
			</Box>
		</Paper>
	);
}